import { useEffect, useState } from 'react';
import { useUser } from '@civic/auth-web3/react';
import { useNavigate } from 'react-router-dom';
import { post } from '../utils/api';
import { useWallet } from './useWallet';
import logger from '../utils/logger';

// Function to save user data to our database
async function saveUserToDatabase(userData, walletAddress) {
  if (!walletAddress) {
    logger.error('No wallet address provided in user data');
    throw new Error('Wallet address is required');
  }

  try {
    logger.log('Saving user data via API:', {
      civicId: userData.id,
      walletAddress,
      email: userData.email || 'No email provided'
    });

    const resp = await post('/api/auth/civic', {
      walletAddress,
      authToken: userData.authToken || userData.idToken || null,
      email: userData.email || null,
      civicId: userData.id,
      name: userData.name || null,
      picture: userData.picture || null,
    });

    logger.log('User data saved successfully via API:', resp?.user);
    return resp?.user;
  } catch (error) {
    logger.error('Error saving user to database:', {
      message: error.message,
      stack: error.stack,
      userData: {
        civicId: userData.id,
        walletAddress,
        email: userData.email
      }
    });
    throw error; // Re-throw to be handled by the caller
  }
}

export function useAuth() {
  const { user, authStatus, error, signIn, signOut } = useUser();
  const { wallet } = useWallet();
  const [isSaving, setIsSaving] = useState(false);
  const navigate = useNavigate();

  // Get the wallet address from the wallet object
  const walletAddress = wallet?.address;

  // Effect to handle saving user data when authenticated
  useEffect(() => {
    const saveUserData = async () => {
      if (authStatus === 'authenticated' && user && walletAddress) {
        logger.log('User authenticated, saving to database...', { 
          user, 
          walletAddress
        });
        
        setIsSaving(true);
        
        try {
          logger.log('Using wallet address from useWallet:', walletAddress);
          
          await saveUserToDatabase({
            id: user.id, // Civic user ID
            email: user.email || '',
            name: user.name || '',
            picture: user.picture || null,
            authToken: user.authToken || user.idToken || 'no-token-available'
          }, walletAddress);
          
          logger.log('User data saved successfully');
        } catch (error) {
          logger.error('Error in saveUserData:', error);
          // You might want to show an error to the user here
        } finally {
          setIsSaving(false);
        }
      }
    };

    saveUserData();
  }, [authStatus, user]);

  // Debug: log the complete user object with all its properties
  useEffect(() => {
    if (user) {
      logger.log('=== CIVIC USER OBJECT ===');
      logger.log('Top-level properties:', Object.keys(user));
      
      // Log the complete user object as JSON for inspection
      try {
        logger.log('Complete user object:', JSON.parse(JSON.stringify(user, (key, value) => {
          // Handle non-serializable values like functions
          return typeof value === 'function' ? '[Function]' : value;
        }, 2)));
      } catch (e) {
        logger.error('Could not stringify user object:', e);
      }
      
      // Log specific properties we're interested in
      logger.log('User ID:', user.id);
      logger.log('Email:', user.email);
      logger.log('Has authToken:', 'authToken' in user);
      logger.log('Has idToken:', 'idToken' in user);
      logger.log('Has publicKey:', 'publicKey' in user);
      
      // Log any nested objects
      if (user.identity) {
        logger.log('Identity object:', user.identity);
      }
      
      logger.log('=== END CIVIC USER OBJECT ===');
    }
  }, [user]);

  const email = user?.email || '';

  return {
    email,
    isAuthenticated: authStatus === 'authenticated',
    isLoading: authStatus === 'authenticating' || isSaving,
    error,
    login: signIn,
    logout: signOut,
    user // Expose the full user object
  };
}